import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { FileDown, Printer, Save, Wand2, Loader2 } from "lucide-react";
import { TIP_OPTIONS, TIME_TIPS, monthMeta, categorize, formatH, curMonth } from "@/lib/sihtarica";
import { exportCSV } from "@/lib/format";

function SummaryCard({ label, value, accent }) {
  return (
    <Card className="p-4 border">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-xl font-extrabold font-heading tabular-nums mt-1 ${accent || ""}`}>{value}</div>
    </Card>
  );
}

export default function Sihtarica() {
  const [workers, setWorkers] = useState([]);
  const [workerId, setWorkerId] = useState("");
  const [month, setMonth] = useState(curMonth());
  const [days, setDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fillingAll, setFillingAll] = useState(false);
  const [loading, setLoading] = useState(false);

  const _p = (n) => String(n).padStart(2, "0");
  const _today = new Date();
  const todayStr = `${_today.getFullYear()}-${_p(_today.getMonth() + 1)}-${_p(_today.getDate())}`;
  const _nowMin = _today.getHours() * 60 + _today.getMinutes();
  const defaultDay = (m) => {
    if (m.date > todayStr) return { ...m, tip: "", start: "", end: "", odmor: 0, overtime: 0, note: "" };
    if (m.weekend) return { ...m, tip: "Sedmični odmor", start: "", end: "", odmor: 0, overtime: 0, note: "" };
    if (m.date === todayStr) {
      const end = _nowMin >= 17 * 60 ? "17:00" : "";
      return { ...m, tip: "Rad", start: "08:00", end, odmor: 1, overtime: 0, note: "" };
    }
    return { ...m, tip: "Rad", start: "08:00", end: "17:00", odmor: 1, overtime: 0, note: "" };
  };

  useEffect(() => {
    api.get("/workers").then((r) => {
      const act = r.data.filter((w) => w.active !== "Neaktivan").sort((a, b) => a.name.localeCompare(b.name));
      setWorkers(act);
      if (act[0]) setWorkerId(act[0].id);
    });
  }, []);

  const loadMonth = () => {
    if (!workerId || !month) return;
    setLoading(true);
    const meta = monthMeta(month);
    api
      .get("/evidencija", { params: { month, worker_id: workerId } })
      .then((r) => {
        const saved = {};
        (r.data.days || []).forEach((d) => (saved[d.day] = d));
        setDays(
          meta.map((m) => {
            const s = saved[m.day];
            if (s) return { ...m, ...s };
            return defaultDay(m);
          })
        );
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadMonth(); /* eslint-disable-next-line */ }, [workerId, month]);

  const fillAllWorkers = async () => {
    if (!month || workers.length === 0) return;
    setFillingAll(true);
    try {
      const meta = monthMeta(month);
      for (const w of workers) {
        const r = await api.get("/evidencija", { params: { month, worker_id: w.id } });
        const saved = {};
        (r.data.days || []).forEach((d) => (saved[d.day] = d));
        const days = meta.map((m) => {
          const s = saved[m.day];
          if (s) return { ...m, ...s };
          return defaultDay(m);
        });
        await api.put("/evidencija", { worker_id: w.id, month, days });
      }
      toast.success("Popunjeno i spremljeno za sve radnike (08:00–17:00)");
      loadMonth();
    } catch (e) {
      toast.error("Greška pri popunjavanju");
    } finally {
      setFillingAll(false);
    }
  };

  const setDay = (day, patch) => setDays((prev) => prev.map((d) => (d.day === day ? { ...d, ...patch } : d)));

  const totals = useMemo(() => {
    const t = { redovan: 0, teren: 0, nocni: 0, prekovremeni: 0, godisnji: 0, bol42m: 0, bol42v: 0, praznik_n: 0, praznik_r: 0, placeno: 0, neplaceno: 0 };
    let topli = 0, totalH = 0;
    days.forEach((d) => {
      const c = categorize(d);
      Object.keys(t).forEach((k) => (t[k] += c[k]));
      topli += c._worked_day;
      totalH += c._total_hours;
    });
    return { t, topli, totalH };
  }, [days]);

  const save = async () => {
    if (!workerId) return;
    setSaving(true);
    try {
      await api.put("/evidencija", { worker_id: workerId, month, days });
      toast.success("Evidencija spremljena");
    } catch (e) {
      toast.error("Greška pri spremanju");
    } finally {
      setSaving(false);
    }
  };

  const fillStandard = () => setDays((prev) => prev.map((d) => defaultDay(d)));

  const worker = workers.find((w) => w.id === workerId);

  const handleExport = () => {
    const headers = ["Dan", "Datum", "Dan u sedmici", "Tip", "Početak", "Završetak", "Odmor(h)", "Prekovremeni(h)", "Ukupno(h)"];
    const rows = days.map((d) => [d.day, d.date, d.weekday, d.tip, d.start, d.end, d.odmor, d.overtime, categorize(d)._total_hours]);
    exportCSV(`evidencija_${(worker?.name || "").replace(/\s+/g, "_")}_${month}`, headers, rows);
    toast.success("Excel (CSV) preuzet");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading tracking-tight">Šihtarica — Evidencija radnog vremena</h1>
          <p className="text-sm text-muted-foreground mt-1">PELESIĆ d.o.o. Tuzla · unesite dane za radnika i mjesec</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 no-print">
          <Button variant="outline" size="sm" onClick={fillAllWorkers} disabled={fillingAll} data-testid="sihtarica-fill-all-btn">{fillingAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Popuni sve radnike</Button>
          <Button variant="outline" size="sm" onClick={fillStandard} data-testid="sihtarica-fill-btn"><Wand2 className="h-4 w-4 mr-1.5" /> Popuni standardno</Button>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="sihtarica-export-excel"><FileDown className="h-4 w-4 mr-1.5" /> Excel</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="sihtarica-export-pdf"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="sihtarica-save-btn">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Spremi</Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 no-print">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Radnik</label>
          <Select value={workerId} onValueChange={setWorkerId}>
            <SelectTrigger className="w-full sm:w-64" data-testid="sihtarica-worker-select"><SelectValue placeholder="Odaberi radnika" /></SelectTrigger>
            <SelectContent className="bg-popover">
              {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mjesec</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-full sm:w-48" data-testid="sihtarica-month-input" />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard label="Ukupno sati rada" value={formatH(totals.t.redovan + totals.t.teren + totals.t.nocni + totals.t.praznik_r)} accent="text-slate-900 dark:text-slate-100" />
        <SummaryCard label="Prekovremeni" value={formatH(totals.t.prekovremeni)} accent="text-amber-600" />
        <SummaryCard label="Godišnji odmor" value={formatH(totals.t.godisnji)} accent="text-blue-600" />
        <SummaryCard label="Bolovanje" value={formatH(totals.t.bol42m + totals.t.bol42v)} accent="text-red-600" />
        <SummaryCard label="Praznik" value={formatH(totals.t.praznik_n + totals.t.praznik_r)} />
        <SummaryCard label="Dana topli obrok" value={totals.topli} accent="text-emerald-600" />
      </div>

      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead className="w-[110px]">Datum</TableHead>
                <TableHead className="w-[170px]">Tip dana</TableHead>
                <TableHead>Početak</TableHead>
                <TableHead>Završetak</TableHead>
                <TableHead>Odmor (h)</TableHead>
                <TableHead>Prekovremeni (h)</TableHead>
                <TableHead className="text-right">Ukupno (h)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Učitavanje...</TableCell></TableRow>
              ) : (
                days.map((d) => {
                  const timeEnabled = TIME_TIPS.includes(d.tip);
                  const total = categorize(d)._total_hours;
                  return (
                    <TableRow key={d.day} className={d.weekend ? "bg-amber-50/40 dark:bg-amber-950/10" : ""} data-testid="sihtarica-row">
                      <TableCell className="whitespace-nowrap">
                        <span className="font-semibold tabular-nums">{String(d.day).padStart(2, "0")}.</span>
                        <span className={`ml-1.5 text-xs ${d.weekend ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>{d.weekday}</span>
                      </TableCell>
                      <TableCell>
                        <Select value={d.tip} onValueChange={(v) => setDay(d.day, { tip: v })}>
                          <SelectTrigger className="h-9" data-testid={`sihtarica-tip-${d.day}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent className="bg-popover">
                            {TIP_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input type="text" inputMode="numeric" maxLength={5} placeholder="08:00" value={d.start || ""} disabled={!timeEnabled} onChange={(e) => setDay(d.day, { start: e.target.value })} className="h-9 w-[80px]" data-testid={`sihtarica-start-${d.day}`} /></TableCell>
                      <TableCell><Input type="text" inputMode="numeric" maxLength={5} placeholder="17:00" value={d.end || ""} disabled={!timeEnabled} onChange={(e) => setDay(d.day, { end: e.target.value })} className="h-9 w-[80px]" data-testid={`sihtarica-end-${d.day}`} /></TableCell>
                      <TableCell><Input type="number" step="0.5" value={d.odmor ?? ""} disabled={!timeEnabled} onChange={(e) => setDay(d.day, { odmor: e.target.value })} className="h-9 w-[80px]" /></TableCell>
                      <TableCell><Input type="number" step="0.5" value={d.overtime ?? ""} onChange={(e) => setDay(d.day, { overtime: e.target.value })} className="h-9 w-[90px]" /></TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{formatH(total)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
